import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle, BookOpen, FileText, Users, X,
} from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import { CostCard } from '@/components/billing/CostCard'
import { CreditStatusBadge } from '@/components/billing/CreditStatusBadge'
import { ImportActionBar } from '../../components/ImportActionBar'
import { SessionExpirySnackbar } from '../../components/SessionExpirySnackbar'
import { useImportWizardContext } from '../../context/ImportWizardContext'
import { useAudienceImportSessionMutations } from '../../hooks/useAudienceImportSession'
import { usePreviewProjection } from '../../hooks/usePreviewProjection'
import { useCreditInfo } from '@/lib/hooks'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import { usePreviewFilterSelection, computeActiveFromHidden } from '../../hooks/usePreviewFilterSelection'
import { persistPendingCreateIntent } from '../../lib/import-session-route-handoff'
import { expandCustomLabelsForCreate } from '../../lib/group-label-overrides'
import { startAudienceHandoff } from '../../lib/import-session-client-metrics'
import { getApiErrorMessage } from '@/lib/get-api-error-message'

const AUDIENCE_COST_PER_RESPONDENT = 1

export function ConfirmationStep() {
  const { t } = useTranslation('app')
  
  const {
    sessionId,
    snapshot,
    mutateSnapshot,
    goToStep,
    hiddenFilterColumns,
    customFilterLabels,
    audienceTags,
  } = useImportWizardContext()

  const {
    isCreating,
    retryCreate,
    lastMutationRequestId,
  } = useAudienceImportSessionMutations(sessionId, {
    fallbackMachineVersion: snapshot?.machineVersion,
  })

  const {
    projection,
    isLoading: isProjectionLoading,
    error: projectionError,
  } = usePreviewProjection(sessionId, { lastMutationRequestId })

  const { filterChipItems, filterGroups } = usePreviewFilterSelection(projection, {
    persistedLabels: customFilterLabels,
  })
  const resolvedFilterColumns = useMemo(
    () => computeActiveFromHidden(hiddenFilterColumns, filterChipItems),
    [hiddenFilterColumns, filterChipItems],
  )

  const { data: creditData, isLoading: isCreditLoading } = useCreditInfo()

  const [actionError, setActionError] = useState<string | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const flowActions = snapshot?.actions
  const flowLinks = snapshot?.links
  const respondentCount = projection?.respondentCount ?? 0
  const audienceName = projection?.audienceDraft?.name ?? 'Untitled audience'
  const audienceType = projection?.audienceDraft?.audienceType ?? null
  const datasetFileName = projection?.fileSummary?.fileName ?? 'Unknown file'
  const totalCost = Math.ceil(respondentCount * AUDIENCE_COST_PER_RESPONDENT)
  const predictedStatusRoute = flowLinks?.status ?? `/audiences/imports/${sessionId}/status`
  const contextFiles = projection?.contextMaterials?.files ?? []
  const contextFileCount = projection?.contextMaterials?.fileCount ?? 0

  const creditAvailable = creditData?.available ?? 0
  const creditsLoading = isCreditLoading || !creditData
  const allowOverage = creditData?.allowOverage ?? false
  const hasSufficientCredits = creditAvailable >= totalCost
  const willUseOverage = !hasSufficientCredits && allowOverage

  const expiresAtMs = projection?.expiresAt ? new Date(projection.expiresAt).getTime() : null
  const isExpiringSoon =
    expiresAtMs != null && expiresAtMs > now && expiresAtMs - now <= 15 * 60 * 1000
  const isExpired = snapshot?.machineState === 'expired'

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  // Mount guard: redirect if session is not eligible for creation.
  // StatusStep already renders a dedicated expired UI with "Start a new import" CTA.
  useEffect(() => {
    if (!snapshot) return
    if (isExpired) {
      goToStep('creating')
      return
    }
    const createState = snapshot.branchSummaries?.create?.state
    if (createState === 'completed' || createState === 'creating' || createState === 'queued' || createState === 'finalizing') {
      goToStep('creating')
    }
  }, [snapshot, isExpired, goToStep])

  const handleConfirmCreate = useCallback(async () => {
    if (!sessionId) return
    setActionError(null)
    setShowConfirmDialog(false)
    try {
      persistPendingCreateIntent({
        sessionId,
        activeFilterColumns: resolvedFilterColumns,
        customLabels: expandCustomLabelsForCreate(customFilterLabels, filterGroups),
        audienceTags,
      })
      startAudienceHandoff({
        sessionId,
        sourceRoute: 'confirmation-step',
        trigger: 'create_audience',
      })
      await mutateSnapshot(
        (current) =>
          current
            ? {
                ...current,
                route: predictedStatusRoute,
                machineState: 'creating',
                wizardPhase: 'create_running',
                actions: {
                  ...current.actions,
                  canCreate: false,
                  canRetryCreate: false,
                },
                branchSummaries: {
                  ...current.branchSummaries,
                  create: {
                    ...current.branchSummaries.create,
                    state: 'queued',
                    phase: 'queued',
                    errorMessage: null,
                    summaryMessage:
                      current.branchSummaries.create.summaryMessage ??
                      'Waiting for a create worker to claim this run.',
                    queuedAt:
                      current.branchSummaries.create.queuedAt ??
                      new Date().toISOString(),
                  },
                },
              }
            : current,
        { revalidate: false },
      )
      goToStep('creating')
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Audience creation failed.'))
    }
  }, [sessionId, resolvedFilterColumns, customFilterLabels, audienceTags, filterGroups, mutateSnapshot, predictedStatusRoute, goToStep])

  const handleRetryCreate = useCallback(async () => {
    if (!sessionId) return
    setActionError(null)
    try {
      startAudienceHandoff({
        sessionId,
        sourceRoute: 'confirmation-step',
        trigger: 'retry_create',
      })
      await retryCreate()
      goToStep('creating')
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Retry failed.'))
    }
  }, [sessionId, retryCreate, goToStep])

  if (isProjectionLoading && !projection) {
    return (
      <>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-7 w-64 mx-auto" />
        <Skeleton className="h-5 w-80" />
        <Skeleton className="h-[300px] rounded-2xl" />
      </>
    )
  }

  if (projectionError || !projection || !snapshot) {
    return (
      <Alert variant="destructive" className="rounded-2xl">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {projectionError instanceof Error
            ? getApiErrorMessage(projectionError, 'Could not load audience details.')
            : 'Could not load audience details.'}
        </AlertDescription>
      </Alert>
    )
  }

  const canCreate =
    flowActions?.canCreate &&
    !isCreating &&
    !creditsLoading &&
    (hasSufficientCredits || willUseOverage)
  const isRetryPath = flowActions?.canRetryCreate === true

  return (
    <>
      <div className="flex flex-1 flex-col gap-10 motion-safe:animate-wizard-enter-content">
        {actionError && (
          <Alert variant="destructive" className="rounded-2xl">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        )}

        {isExpiringSoon && (
          <SessionExpirySnackbar message="This temporary session expires after inactivity. Viewing or interacting with the flow extends the working window." />
        )}

        <h1 className="font-display text-4xl font-normal leading-[1.2] tracking-[-0.02em] text-foreground dark:text-white">
          {t('audienceCreate.confirmTitle')}
        </h1>
        <p className="text-lg text-muted-foreground dark:text-[#BDBDBD] tracking-tight -mt-6">
          {t('audienceCreate.confirmSubtitle')}
        </p>

        <div className="flex flex-col md:flex-row gap-4">
          {/* Top / left card — audience summary */}
          <div className="flex-1 min-w-0">
            <div className="bg-card rounded-2xl p-7 space-y-5">
              <h3 className="text-lg font-medium text-foreground dark:text-white">{t('audienceCreate.audienceSummary')}</h3>

              <div className="flex flex-col sm:flex-row items-stretch gap-5">
                <SummaryColumn
                  icon={<Users className="h-3 w-3" />}
                  label={t('audienceCreate.audienceLabel')}
                  value={audienceName}
                />
                <VerticalDivider />
                <SummaryColumn
                  icon={<Users className="h-3 w-3" />}
                  label={t('audienceCreate.audienceType')}
                  value={audienceType ?? '—'}
                  muted={!audienceType}
                />
              </div>

              <div className="h-px bg-border" />

              <div className="flex flex-col sm:flex-row items-stretch gap-5">
                <SummaryColumn
                  icon={<FileText className="h-3 w-3" />}
                  label={t('audienceCreate.coreDataset')}
                  value={datasetFileName}
                />
                <VerticalDivider />
                {contextFileCount > 0 ? (
                  <SummaryColumn
                    icon={<BookOpen className="h-3 w-3" />}
                    label={t('audienceCreate.contextMaterials')}
                    value={contextFiles.length > 0
                      ? contextFiles.map((f) => f.originalFilename).join(', ')
                      : t('audienceCreate.files', { count: contextFileCount })}
                  />
                ) : (
                  <SummaryColumn
                    icon={<BookOpen className="h-3 w-3" />}
                    label={t('audienceCreate.contextMaterials')}
                    value={t('audienceCreate.noneAdded')}
                    muted
                  />
                )}
              </div>
            </div>
          </div>

          {/* Bottom / right card — cost card */}
          <CostCard
            respondentCount={respondentCount}
            costPerRespondent={AUDIENCE_COST_PER_RESPONDENT}
            totalCost={totalCost}
            creditStatus={
              <CreditStatusBadge
                totalCost={totalCost}
                available={creditAvailable}
                allowOverage={allowOverage}
                isLoading={creditsLoading}
              />
            }
          />
        </div>

        {isRetryPath ? (
          <ImportActionBar
            nextLabel={t('audienceCreate.retryCreate')}
            onNext={() => void handleRetryCreate()}
            nextDisabled={isCreating}
            nextLoading={isCreating}
            nextLoadingLabel={t('audienceCreate.retryingCreate')}
          />
        ) : (
          <ImportActionBar
            backLabel={t('audienceCreate.steps.segmentation')}
            onBack={() => goToStep('segmentation')}
            backDisabled={isCreating}
            backVariant="secondary"
            nextLabel={t('audienceCreate.confirmCreate')}
            onNext={() => setShowConfirmDialog(true)}
            nextDisabled={!canCreate}
            nextLoading={isCreating}
            nextLoadingLabel={t('audienceCreate.creatingAudience')}
          />
        )}
      </div>

      {/* Creation confirmation dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-card border-border dark:border-[hsl(var(--card-inset))] max-w-[540px] rounded-2xl shadow-[0px_4px_19.6px_0px_rgba(0,0,0,0.08)] p-0 gap-0">
          <div className="flex items-center justify-end p-3">
            <button
              type="button"
              onClick={() => setShowConfirmDialog(false)}
              className="text-muted-foreground hover:text-foreground dark:text-white/60 dark:hover:text-white transition-colors"
              aria-label={t('audienceCreate.close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-col items-center gap-7 px-10 pb-10">
            <AlertDialogHeader className="text-center sm:text-center space-y-3 w-full">
              <AlertDialogTitle className="font-display text-[24px] font-normal text-foreground dark:text-white tracking-[-0.48px] leading-[1.2]">
                {t('audienceCreate.readyToLaunch')}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="flex flex-col items-center gap-1">
                  <p className="text-[16px] leading-[1.6] text-foreground dark:text-white">
                    {totalCost > 0
                      ? t('audienceCreate.creatingNamedCost', {
                          name: audienceName,
                          respondents: respondentCount.toLocaleString(),
                          credits: totalCost.toLocaleString(),
                        })
                      : t('audienceCreate.creatingNamed', {
                          name: audienceName,
                          respondents: respondentCount.toLocaleString(),
                        })}
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter className="flex-row justify-center sm:justify-center gap-4 sm:space-x-0">
              <AlertDialogAction
                variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
                onClick={() => void handleConfirmCreate()}
                disabled={isCreating}
                className={cn('h-10 rounded-[6px] px-4', AUDIENCE_WIZARD_BUTTON_CLASS)}
              >
                {isCreating ? t('audienceCreate.creating') : t('audienceCreate.create')}
              </AlertDialogAction>
              <AlertDialogCancel
                disabled={isCreating}
                className="mt-0 sm:mt-0 h-10 rounded-[6px] px-4 text-[16px] font-medium border-primary dark:border-input-focus bg-transparent text-primary dark:text-input-focus hover:bg-primary/10 dark:hover:bg-input-focus/10"
              >
                {t('audienceCreate.cancel')}
              </AlertDialogCancel>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function SummaryColumn({ icon, label, value, muted }: {
  icon: React.ReactNode
  label: string
  value: string
  muted?: boolean
}) {
  return (
    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex items-center gap-1">
        <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-primary/10 text-primary flex-shrink-0">
          {icon}
        </span>
        <span className="text-sm text-muted-foreground dark:text-[#BDBDBD]">{label}</span>
      </div>
      <p className={muted
        ? "text-base text-muted-foreground dark:text-[#BDBDBD] truncate"
        : "text-base font-semibold text-foreground dark:text-white truncate"
      }>
        {value}
      </p>
    </div>
  )
}

function VerticalDivider() {
  return <div className="hidden sm:block w-px self-stretch bg-border" />
}
